import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

import { useAuth } from '../../contexts/AuthContext';
import { vetrineService } from '../../services/vetrineService';

const AdminVetrine = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    categoria: '',
    zona: '',
    status: '',
    inEvidenza: false
  });
  const [formData, setFormData] = useState({
    nomeNegozio: '',
    categoria: '',
    zona: '',
    indirizzo: '',
    telefono: '',
    whatsapp: '',
    instagram: '',
    facebook: '',
    sitoWeb: '',
    descrizioneBreve: '',
    orariApertura: '',
    rating: '',
    numeroRecensioni: '',
    inEvidenza: false,
    cashback: '',
    servizi: {
      consegna: false,
      prenotazione: false,
      appuntamento: false,
      pagamentiDigitali: false,
      convenzioni: false
    },
    prodotto1: '',
    prodotto2: '',
    prodotto3: '',
    promoTitolo: '',
    promoDescrizione: '',
    promoScadenza: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userRole !== 'admin') {
      navigate('/');
      return;
    }

    loadData();
  }, [user, userRole, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [storesData, categoriesData, zonesData] = await Promise.all([
        vetrineService?.getAllStores(),
        vetrineService?.getCategories(),
        vetrineService?.getZones()
      ]);
      setStores(storesData);
      setCategories(categoriesData);
      setZones(zonesData);
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceChange = (service, checked) => {
    setFormData(prev => ({
      ...prev,
      servizi: {
        ...prev?.servizi,
        [service]: checked
      }
    }));
  };

  const resetForm = () => {
    setFormData({
      nomeNegozio: '',
      categoria: '',
      zona: '',
      indirizzo: '',
      telefono: '',
      whatsapp: '',
      instagram: '',
      facebook: '',
      sitoWeb: '',
      descrizioneBreve: '',
      orariApertura: '',
      rating: '',
      numeroRecensioni: '',
      inEvidenza: false,
      cashback: '',
      servizi: {
        consegna: false,
        prenotazione: false,
        appuntamento: false,
        pagamentiDigitali: false,
        convenzioni: false
      },
      prodotto1: '',
      prodotto2: '',
      prodotto3: '',
      promoTitolo: '',
      promoDescrizione: '',
      promoScadenza: ''
    });
    setEditingStore(null);
    setShowForm(false);
  };

  const handleEdit = (store) => {
    setFormData({
      nomeNegozio: store?.nomeNegozio || '',
      categoria: store?.categoria || '',
      zona: store?.zona || '',
      indirizzo: store?.indirizzo || '',
      telefono: store?.telefono || '',
      whatsapp: store?.whatsapp || '',
      instagram: store?.instagram || '',
      facebook: store?.facebook || '',
      sitoWeb: store?.sitoWeb || '',
      descrizioneBreve: store?.descrizioneBreve || '',
      orariApertura: store?.orariApertura || '',
      rating: store?.rating || '',
      numeroRecensioni: store?.numeroRecensioni || '',
      inEvidenza: store?.inEvidenza || false,
      cashback: store?.cashback || '',
      servizi: store?.servizi || {
        consegna: false,
        prenotazione: false,
        appuntamento: false,
        pagamentiDigitali: false,
        convenzioni: false
      },
      prodotto1: store?.prodotto1 || '',
      prodotto2: store?.prodotto2 || '',
      prodotto3: store?.prodotto3 || '',
      promoTitolo: store?.promoTitolo || '',
      promoDescrizione: store?.promoDescrizione || '',
      promoScadenza: store?.promoScadenza || ''
    });
    setEditingStore(store);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    if (!formData?.nomeNegozio || !formData?.categoria || !formData?.zona || !formData?.indirizzo || !formData?.telefono) {
      setError('Compila tutti i campi obbligatori');
      return;
    }

    try {
      if (editingStore) {
        await vetrineService?.updateStore(editingStore?.id, formData);
        setSuccess('Vetrina aggiornata con successo');
      } else {
        await vetrineService?.createStore(formData);
        setSuccess('Vetrina creata con successo');
      }
      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.message || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa vetrina?')) return;

    try {
      await vetrineService?.deleteStore(storeId);
      setSuccess('Vetrina eliminata con successo');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Errore nell\'eliminazione');
    }
  };

  const filteredStores = stores?.filter(store => {
    if (filters?.search && !store?.nomeNegozio?.toLowerCase()?.includes(filters?.search?.toLowerCase())) {
      return false;
    }
    if (filters?.categoria && store?.categoria !== filters?.categoria) {
      return false;
    }
    if (filters?.zona && store?.zona !== filters?.zona) {
      return false;
    }
    if (filters?.status && store?.status !== filters?.status) {
      return false;
    }
    if (filters?.inEvidenza && !store?.inEvidenza) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Caricamento... - Admin Vetrine</title>
        </Helmet>
        <div className="flex flex-col min-h-screen bg-gray-50">
          <Header onFilterClick={() => {}} />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Icon name="loader" className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Caricamento...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestione Vetrine - Admin</title>
      </Helmet>
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header onFilterClick={() => {}} />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Gestione Vetrine</h1>
            <Button
              onClick={() => navigate('/admin-storefront-form')}
              className="flex items-center gap-2"
            >
              <Icon name="add" className="w-5 h-5" />
              <span>Nuova Vetrina</span>
            </Button>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <Icon name="error" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <Icon name="check_circle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-green-800">{success}</p>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Input
                placeholder="Cerca per nome..."
                value={filters?.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e?.target?.value }))}
              />
              <Select
                value={filters?.categoria}
                onChange={(e) => setFilters(prev => ({ ...prev, categoria: e?.target?.value }))}
              >
                <option value="">Tutte le categorie</option>
                {categories?.map(cat => (
                  <option key={cat?.id} value={cat?.value}>{cat?.label}</option>
                ))}
              </Select>
              <Select
                value={filters?.zona}
                onChange={(e) => setFilters(prev => ({ ...prev, zona: e?.target?.value }))}
              >
                <option value="">Tutte le zone</option>
                {zones?.map(zone => (
                  <option key={zone?.id} value={zone?.value}>{zone?.label}</option>
                ))}
              </Select>
              <Select
                value={filters?.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e?.target?.value }))}
              >
                <option value="">Tutti gli stati</option>
                <option value="attiva">Attiva</option>
                <option value="bozza">Bozza</option>
                <option value="disattivata">Disattivata</option>
              </Select>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="filterInEvidenza"
                  checked={filters?.inEvidenza}
                  onChange={(e) => setFilters(prev => ({ ...prev, inEvidenza: e?.target?.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="filterInEvidenza" className="text-sm text-gray-700">
                  Solo in evidenza
                </label>
              </div>
            </div>
          </div>

          {/* Stores List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Negozio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Zona
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      In Evidenza
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStores?.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        Nessuna vetrina trovata
                      </td>
                    </tr>
                  ) : (
                    filteredStores?.map(store => (
                      <tr key={store?.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {store?.nomeNegozio}
                              </div>
                              <div className="text-sm text-gray-500">
                                {store?.indirizzo}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {categories?.find(c => c?.value === store?.categoria)?.label || store?.categoria}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">
                            {zones?.find(z => z?.value === store?.zona)?.label || store?.zona}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            store?.status === 'attiva' ? 'bg-green-100 text-green-800' :
                            store?.status === 'bozza' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {store?.status || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {store?.inEvidenza ? (
                            <Icon name="star" className="w-5 h-5 text-yellow-500" />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/admin-content-management?storeId=${store?.id}`)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Gestisci Contenuti"
                            >
                              <Icon name="Package" className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => navigate(`/admin-storefront-form?id=${store?.id}`)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Modifica"
                            >
                              <Icon name="edit" className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(store?.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Elimina"
                            >
                              <Icon name="delete" className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">Totale Vetrine</div>
              <div className="text-2xl font-bold text-gray-900">{stores?.length || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">Attive</div>
              <div className="text-2xl font-bold text-green-600">
                {stores?.filter(s => s?.status === 'attiva')?.length || 0}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">Bozze</div>
              <div className="text-2xl font-bold text-yellow-600">
                {stores?.filter(s => s?.status === 'bozza')?.length || 0}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">In Evidenza</div>
              <div className="text-2xl font-bold text-blue-600">
                {stores?.filter(s => s?.inEvidenza)?.length || 0}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminVetrine;